import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Alert, Button, Card, Chip, Description, Input, Label, ListBox, Meter, Select, Skeleton, Table, Tabs } from '@heroui/react'
import { Chrome } from '../ui/Chrome'
import { Drawer } from '../ui/Drawer'
import { MoveForm } from '../ui/MoveForm'
import { NoteHistory } from '../ui/NoteHistory'
import { mins, rich, useHallToast } from '../ui/hallParts'
import type { HBar } from '../ui/LiveCharts'
import { api } from '../lib/api'
import { bookingWho } from '../lib/bookingDesk'
import { BOOKINGS_MODULE } from '../lib/bookings'
import { byCallingOrder, callingWaitSeconds } from '../lib/callingWait'
import { secondsSince, useNow } from '../lib/clock'
import { requestKindLabel } from '../lib/deskStatus'
import { deskQueueIds, deskRows, knowsCallers, pausedWords, signOutQuestion, type DeskRow } from '../lib/floorBoard'
import { currentServiceId, moveBody, whyNotMove, type MoveChoice } from '../lib/move'
import { DEFAULT_PLACEMENT } from '../lib/transfer'
import { SUPERVISE } from '../lib/putBack'
import { mayChangeProfile, ruleTag } from '../lib/profileSwitch'
import { overServiceLimit } from '../lib/serviceTime'
import { explain } from '../lib/staffErrors'
import { formatMoment, nameIn, type StaffKey, type StaffLanguage } from '../lib/staffText'
import { useBranchLive } from '../lib/useBranch'
import { alertsToShow } from '../lib/queueAlerts'
import type { ConnectionStatus } from '../lib/live'
import type { FloorOverview } from '../lib/floorOverview'
import {
  LONG_SERVING_SEC,
  SERIES_EVERY_MS,
  dashSeries,
  deskCounts,
  deskState,
  freshness,
  journeySteps,
  needsAttention,
  tokenMarks,
  numberPart,
  ordinal,
  queueStatus,
  seriesColour,
  slaLevel,
  slaUsed,
  waitKpiLevel,
  type FloorSeries,
} from '../lib/liveDash'
import { contextOrganisation, onOrganisationChange } from '../lib/ownerOrg'
import type { Appointment, Branch, CounterView, Identity, QueueView, RequestKind, Service, Ticket, TicketHistoryEvent, WorkProfile } from '../lib/types'
import { useStaffText } from '../ui/StaffText'
import { TransferredChip, useColleagues } from '../ui/TransferredChip'
import { AllBranches } from './FloorBranches'
import {
  ActButton,
  BranchPicker,
  ChartRow,
  DashHead,
  KpiRow,
  LiveChip,
  RefreshControls,
  StatusChip,
  TrendRow,
  attentionItems,
  minWord,
  useTimes,
  type AttentionItem,
} from './FloorParts'
import '../styles/hall.css'
import '../styles/floor.css'

/**
 * The live dashboard, for a supervisor: All branches, or one branch chosen in the
 * head's picker. Five figures, waiting over the last hours beside what needs
 * attention, then (for one branch) its desks, its queues and its waiting customers.
 * Held and missed visits are left out; Reception and the Counter handle them.
 *
 * It adds no power: the desk sign-out is ownsDesk's, the visit actions are
 * reception's and the counter's (lib/floorBoard). The figures come from the
 * live channel; the hours behind them from GET /floor/series, read once a
 * minute (lib/liveDash).
 *
 * Waits are calling waits (lib/callingWait), and "over target" is each ticket's
 * own service level, so a line mixing a five- and a thirty-minute service is
 * judged customer by customer.
 */
export function Floor({
  identity,
  onNavigate,
  onSignOut,
}: {
  identity: Identity
  onNavigate: (to: string) => void
  onSignOut: () => void
}) {
  const { lang, say } = useStaffText()
  const canSupervise = identity.permissions.includes(SUPERVISE)
  const [view, setView] = useState(addressedBranch)
  const [overview, setOverview] = useState<FloorOverview | null>(null)
  const [overviewError, setOverviewError] = useState('')
  const [overviewAt, setOverviewAt] = useState(0)
  const [status, setStatus] = useState<ConnectionStatus | undefined>()
  const [branchAt, setBranchAt] = useState(0)
  const [branch, setBranch] = useState<BranchHours | null>(null)
  const [auto, setAuto] = useState(readAuto)
  const [refreshes, setRefreshes] = useState(0)
  const [series, setSeries] = useState<{ scope: string; data: FloorSeries } | null>(null)

  // Every branch's figures: the picker, the All branches view, a branch's Served.
  useEffect(() => {
    if (!canSupervise) return
    let stopped = false
    const read = () => {
      if (document.hidden) return
      api
        .floorBranches(contextOrganisation() || undefined)
        .then((o) => {
          if (stopped) return
          setOverview(o)
          setOverviewAt(Date.now())
          setOverviewError('')
        })
        .catch((err) => !stopped && setOverviewError(explain(err, lang, 'common.unreachable')))
    }
    read()
    const timer = auto ? window.setInterval(read, OVERVIEW_EVERY_MS) : undefined
    const offOrg = onOrganisationChange(() => {
      window.history.replaceState({}, '', '/floor')
      setView('')
      read()
    })
    if (auto) document.addEventListener('visibilitychange', read)
    return () => {
      stopped = true
      window.clearInterval(timer)
      offOrg()
      document.removeEventListener('visibilitychange', read)
    }
    // lang only words a refusal; a language switch must not restart the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSupervise, auto, refreshes])

  useEffect(() => {
    const onPop = () => setView(addressedBranch())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const choose = (id: string) => {
    if (id === view) return
    window.history.pushState({}, '', id ? `/floor?branch=${encodeURIComponent(id)}` : '/floor')
    setStatus(undefined)
    setBranchAt(0)
    setView(id)
  }

  const many = (overview?.branches.length ?? 0) > 1
  // One branch in reach: it is the whole dashboard, with no picker and no All.
  const branchId = view || (overview?.branches.length === 1 ? overview.branches[0]!.branchId : '')
  const scope = branchId || 'all'

  // The last hours: once a minute, on Refresh, and when the scope changes.
  useEffect(() => {
    if (!canSupervise) return
    let stopped = false
    const read = () => {
      if (document.hidden) return
      api
        .floorSeries(branchId || undefined, branchId ? undefined : contextOrganisation() || undefined)
        .then((data) => !stopped && setSeries({ scope, data }))
        .catch(() => undefined)
    }
    read()
    const timer = auto ? window.setInterval(read, SERIES_EVERY_MS) : undefined
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [canSupervise, scope, branchId, auto, refreshes])
  const shownSeries = series?.scope === scope ? series.data : null

  const toggleAuto = (on: boolean) => {
    setAuto(on)
    try {
      window.localStorage.setItem(AUTO_KEY, on ? '1' : '0')
    } catch {
      // A private window keeps the choice for this page only.
    }
  }

  const row = overview?.branches.find((b) => b.branchId === branchId)
  const fresh = branchId
    ? freshness({ connected: status === 'live', failed: false, auto, updatedAt: branchAt })
    : freshness({ connected: Boolean(overview), failed: Boolean(overviewError), auto, updatedAt: overviewAt })

  const chrome = (body: ReactNode) => (
    <Chrome
      title={say('entry.app.floor')}
      staff={identity.staff}
      status={branchId ? status : undefined}
      onNavigate={onNavigate}
      onSignOut={onSignOut}
    >
      {body}
    </Chrome>
  )

  if (!canSupervise) {
    return chrome(
      <main className="page">
        <Notice text={say('floorBoard.supervisorsOnly')} />
      </main>,
    )
  }

  const content = branchId ? (
    <BranchFloor
      key={branchId}
      identity={identity}
      branchId={branchId}
      served={row ? row.served : null}
      series={shownSeries}
      auto={auto}
      refreshes={refreshes}
      onNavigate={onNavigate}
      onStatus={setStatus}
      onUpdated={setBranchAt}
      onBranch={setBranch}
    />
  ) : overview ? (
    <AllBranches overview={overview} series={shownSeries} onChoose={choose} />
  ) : overviewError ? (
    <Notice danger text={overviewError} />
  ) : (
    <Loading />
  )

  const name = row?.name || branch?.name || ''
  const sub = !branchId
    ? say('floor.db.subAll')
    : branch?.openNow === false
      ? say('floor.db.subClosed', { branch: name })
      : branch?.closesAt
        ? say('floor.db.subOpen', { branch: name, time: formatMoment(branch.closesAt, lang, { time: true, zone: branch.timezone }) })
        : say('floor.db.subBranch', { branch: name })

  return chrome(
    <main className="page page--work fl-page">
      <DashHead sub={sub}>
        <LiveChip fresh={fresh} zone={branchId ? branch?.timezone : undefined} />
        {many && overview && (
          <BranchPicker branches={overview.branches} current={branchId} allWaiting={overview.totals.waiting} onChoose={choose} />
        )}
        <RefreshControls auto={auto} onAuto={toggleAuto} onRefresh={() => setRefreshes((n) => n + 1)} />
      </DashHead>
      {content}
    </main>,
  )
}

/** How often the All branches figures are read again. */
const OVERVIEW_EVERY_MS = 15_000
/** This browser's Auto-refresh choice. */
const AUTO_KEY = 'qms.floor.auto'

/** The tallest the element has been: a tab switch must not shorten the page under the reader. */
function useTallest<T extends HTMLElement>() {
  const [el, setEl] = useState<T | null>(null)
  const [min, setMin] = useState(0)
  useEffect(() => {
    if (!el) return
    const grow = () => setMin((m) => Math.max(m, el.offsetHeight))
    grow()
    const watch = new ResizeObserver(grow)
    watch.observe(el)
    return () => watch.disconnect()
  }, [el])
  return [setEl, min] as const
}

function readAuto(): boolean {
  try {
    return window.localStorage.getItem(AUTO_KEY) !== '0'
  } catch {
    return true
  }
}

/** The branch's lists, one tab at a time. */
type ListTab = 'queues' | 'desks' | 'waiting'
/** How long after an action a paused dashboard still follows the live channel, so the change shows. */
const FOLLOW_MS = 4_000

/** What the head says of the branch in view. */
type BranchHours = Pick<Branch, 'name' | 'timezone' | 'openNow' | 'closesAt'>

/** The branch the address names (`/floor?branch=<id>`), or '' for All branches. */
function addressedBranch(): string {
  return new URLSearchParams(window.location.search).get('branch') ?? ''
}

/** A line the dashboard cannot draw around: a refusal, or why there is nothing to show. */
function Notice({ text, danger }: { text: string; danger?: boolean }) {
  return (
    <Alert status={danger ? 'danger' : 'default'} className="fl-error" role={danger ? 'alert' : undefined}>
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{text}</Alert.Title>
      </Alert.Content>
    </Alert>
  )
}

/** The dashboard's shape while its first figures are on their way. */
function Loading() {
  return (
    <div className="fl-loading" aria-busy="true">
      <Skeleton className="fl-loading__kpis" />
      <div className="fl-grid2">
        <Skeleton className="fl-loading__card" />
        <Skeleton className="fl-loading__card" />
      </div>
    </div>
  )
}

/** A share as a short meter with its figure beside it. */
function Share({ value, color }: { value: number; color: 'default' | 'success' | 'warning' | 'danger' }) {
  return (
    <span className="fl-share">
      {value}%
      <Meter aria-label={`${value}%`} value={value} size="sm" color={color}>
        <Meter.Track>
          <Meter.Fill />
        </Meter.Track>
      </Meter>
    </span>
  )
}

/** Below this share of a queue's waiting customers inside their target, its meter turns amber. */
const MET_WARN_PCT = 80

const DESK_COLOR = { serving: 'accent', free: 'success', break: 'warning', closed: 'default' } as const

/** One branch's live floor: its own socket, desks, queues and waiting customers. */
function BranchFloor({
  identity,
  branchId: chosen,
  served,
  series,
  auto,
  refreshes,
  onNavigate,
  onStatus,
  onUpdated,
  onBranch,
}: {
  identity: Identity
  branchId: string
  served: number | null
  series: FloorSeries | null
  auto: boolean
  refreshes: number
  onNavigate: (to: string) => void
  onStatus: (status: ConnectionStatus) => void
  onUpdated: (at: number) => void
  onBranch: (branch: BranchHours | null) => void
}) {
  const { lang, say } = useStaffText()
  const staff = identity.staff
  const canSupervise = identity.permissions.includes(SUPERVISE)
  const canDecide = identity.permissions.includes('status.approve')
  const live = useBranchLive('floor', { lang })
  const now = useNow()
  const branchId = live.branch?.id ?? ''
  const zone = live.branch?.timezone
  const toast = useHallToast()

  // The request an approval email's link names (`/floor?branch=<id>&request=<id>`).
  const [linked] = useState(() => ({ requestId: new URLSearchParams(window.location.search).get('request') ?? '' }))
  useEffect(() => {
    live.chooseBranch(chosen)
    // Keyed by branch in Floor, so this runs once; chooseBranch is a fresh function every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen])
  useEffect(() => onStatus(live.status), [live.status, onStatus])
  const lb = live.branch
  useEffect(() => {
    onBranch(lb ? { name: lb.name, timezone: lb.timezone, openNow: lb.openNow, closesAt: lb.closesAt } : null)
    // The four fields, not the object: the hook hands a fresh one with every report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lb?.name, lb?.timezone, lb?.openNow, lb?.closesAt, onBranch])

  // Auto-refresh off holds the floor as it was; Refresh and the person's own actions move it on.
  const [held, setHeld] = useState(live.state)
  const followUntil = useRef(0)
  useEffect(() => {
    if (auto || !held || Date.now() < followUntil.current) setHeld(live.state)
    // held is read only to take the first snapshot while paused.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.state, auto])
  useEffect(() => {
    if (!refreshes) return
    setHeld(live.state)
    followUntil.current = Date.now() + FOLLOW_MS
    live.resync()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshes])
  const state = auto ? live.state : held
  useEffect(() => {
    if (state) onUpdated(Date.now())
  }, [state, onUpdated])
  const [tab, setTab] = useState<ListTab>('queues')
  const [listsRef, listsMin] = useTallest<HTMLDivElement>()

  const [services, setServices] = useState<Service[]>([])
  const [profiles, setProfiles] = useState<WorkProfile[]>([])
  const [bookings, setBookings] = useState<Appointment[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [declining, setDeclining] = useState<{ id: string; reason: string } | null>(null)
  const [openTicket, setOpenTicket] = useState('')
  const [openDesk, setOpenDesk] = useState('')
  const [journey, setJourney] = useState('')

  useEffect(() => {
    if (!branchId) return
    api
      .services(branchId)
      .then((r) => setServices(r.services))
      .catch((err) => setError(explain(err, lang, 'common.unreachable')))
    // The published profiles say which queues each desk calls.
    api
      .profiles(branchId)
      .then((r) => setProfiles(r.profiles))
      .catch(() => setProfiles([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId])

  // Bookings whose time has passed and who have not arrived: the live channel carries tickets, not bookings.
  const mayBookings =
    (identity.modules ?? []).includes(BOOKINGS_MODULE) && identity.permissions.includes('ticket.issue')
  useEffect(() => {
    if (!branchId || !mayBookings) return
    const read = () =>
      api
        .appointments(branchId)
        .then((r) => setBookings(r.appointments))
        .catch(() => setBookings([]))
    void read()
    const timer = window.setInterval(read, 60_000)
    return () => window.clearInterval(timer)
  }, [branchId, mayBookings])

  const counters = state?.counters
  const tickets = state?.tickets
  const queues = useMemo(() => [...(state?.queues ?? [])].sort((a, b) => a.sortOrder - b.sortOrder), [state?.queues])
  const snapshotServices = state?.services
  const desks = useMemo(() => deskRows(counters ?? [], staff.id, canSupervise, now), [counters, staff.id, canSupervise, now])
  const deskName = useMemo(() => new Map((counters ?? []).map((c) => [c.counterId, c.name])), [counters])
  const requests = state?.requests ?? []
  const askedAt = (counterId: string) => requests.find((r) => r.counterId === counterId)?.kind
  const colleagues = useColleagues(branchId, identity.permissions)
  const sentChip = (t: Ticket | undefined) => (t ? <TransferredChip ticket={t} counters={counters ?? []} colleagues={colleagues} /> : null)

  // The request an approval email named, brought into view once its snapshot has arrived.
  const linkedRequestId = linked.requestId ? requests.find((r) => r.id === linked.requestId)?.id : undefined
  useEffect(() => {
    if (!linkedRequestId) return
    document.getElementById(`fl-req-${linkedRequestId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [linkedRequestId])

  const serviceOf = (id: string) => snapshotServices?.find((s) => s.id === id) ?? services.find((s) => s.id === id)
  const serviceName = (t: Ticket) => {
    const s = serviceOf(currentServiceId(t))
    return s ? nameIn(s.name, lang, s.code) : ''
  }
  const profileQueues = (desk: CounterView) => deskQueueIds(desk, profiles)
  const known = knowsCallers(counters ?? [], profiles)
  const letter = (q: QueueView) => q.letter || q.code.slice(0, 1)
  const queueName = (q: QueueView) => nameIn(q.name, lang, q.code)

  const waitOf = (t: Ticket) => callingWaitSeconds(t, now)
  const targetOf = (t: Ticket) => t.serviceLevelSec ?? 0

  const open = desks.filter((d) => d.desk.state !== 'closed')
  const working = open.filter((d) => d.desk.state !== 'paused')
  const calls = (d: DeskRow, q: QueueView) => profileQueues(d.desk).includes(q.queueId)
  const waitingFor = (d: DeskRow) => queues.filter((q) => calls(d, q)).reduce((n, q) => n + q.waiting, 0)

  const workFor = (queueId: string) => series?.queues.find((w) => w.queueId === queueId)

  const lines = queues.map((q, i) => {
    const line = byCallingOrder(
      (tickets ?? []).filter((t) => t.state === 'waiting' && t.queueId === q.queueId),
      now,
      live.state?.bookedFirst ?? true,
    )
    const waits = line.map(waitOf)
    const targets = line.map(targetOf).filter((s) => s > 0)
    return {
      q,
      colour: seriesColour(i),
      line,
      open: working.filter((d) => calls(d, q)).length,
      avgWait: waits.length ? waits.reduce((a, b) => a + b, 0) / waits.length : 0,
      longest: Math.max(0, ...waits),
      target: q.serviceLevelSec ?? (targets.length ? Math.min(...targets) : 0),
      over: line.filter((t) => slaLevel(waitOf(t), targetOf(t)) === 'critical').length,
      near: line.filter((t) => slaLevel(waitOf(t), targetOf(t)) === 'warning').length,
    }
  })
  const lineOf = new Map(lines.map((l) => [l.q.queueId, l]))

  const waitingTickets = lines.flatMap((l) => l.line)
  const waitingNow = waitingTickets.length
  const avgWait = waitingNow ? waitingTickets.reduce((n, t) => n + waitOf(t), 0) / waitingNow : 0
  const longest = Math.max(0, ...waitingTickets.map(waitOf))
  const over = lines.reduce((n, l) => n + l.over, 0)
  const near = lines.reduce((n, l) => n + l.near, 0)
  const byWait = [...waitingTickets].sort((a, b) => waitOf(b) - waitOf(a))
  const d = dashSeries(series, { waiting: waitingNow, avgWaitSec: avgWait, longestWaitSec: longest })
  const times = useTimes(d.ends, zone)
  // The chart's target line only when every queue with a target has the same one.
  const targets = new Set(lines.map((l) => l.target).filter((s) => s > 0))
  const sharedTarget = targets.size === 1 ? [...targets][0] : undefined

  const servingSec = (desk: CounterView) => {
    const t = desk.ticketId ? tickets?.find((x) => x.id === desk.ticketId) : undefined
    const started = t?.transactions[t.currentTransaction]?.startedAt
    return desk.ticketState === 'serving' && started ? secondsSince(started, now) : 0
  }
  const longDesks = open.filter((r) => servingSec(r.desk) > LONG_SERVING_SEC)

  const late = bookings
    .filter((b) => b.state === 'booked' && Date.parse(b.startsAt) < now)
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))

  async function act(fn: () => Promise<unknown>, done: string) {
    setError('')
    setBusy(true)
    try {
      await fn()
      followUntil.current = Date.now() + FOLLOW_MS
      toast.show(done)
      return true
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
      return false
    } finally {
      setBusy(false)
    }
  }

  // A manager's Approve or Decline; the server refuses a second decision or one's own.
  async function decide(id: string, approve: boolean, reason?: string) {
    const ok = await act(
      () => api.decideRequest(branchId, id, approve, reason),
      say(approve ? 'hall.done.approved' : 'hall.done.declined'),
    )
    if (ok) setDeclining(null)
  }

  // Needs attention: the dashboard's own list, then requests, queue alerts and late bookings.
  const attention: AttentionItem[] = attentionItems(
    needsAttention({
      unstaffed: known
        ? lines.filter((l) => l.line.length > 0 && l.open === 0).map((l) => ({ name: queueName(l.q), waiting: l.line.length }))
        : [],
      over,
      near,
      long: { n: longDesks.length, who: longDesks.map((r) => [r.desk.name, r.desk.staffName].filter(Boolean).join(' · ')) },
    }),
    say,
    () => {
      setTab('waiting')
      window.requestAnimationFrame(() => document.getElementById('fl-lists')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
    },
  )
  const firstWarning = attention.findIndex((a) => a.level !== 'critical')
  const requestItems: AttentionItem[] = requests.map((r) => {
    const desk = deskName.get(r.counterId) ?? r.counterCode
    const kind = requestKindLabel(r.kind, lang)
    const isDeclining = declining?.id === r.id
    return {
      key: `req-${r.id}`,
      domId: `fl-req-${r.id}`,
      highlight: r.id === linkedRequestId,
      level: 'warning',
      icon: 'pause',
      title: say('floor.db.a.request', { who: r.staffName || desk, kind }),
      sub: [desk, say('hall.requests.waitingFor', { time: mins(secondsSince(r.requestedAt, now), say) })].join(', '),
      action:
        !canDecide || r.staffId === staff.id ? undefined : isDeclining ? (
          <span className="fl-alert__acts">
            <Input
              className="fl-reason"
              aria-label={say('hall.requests.reasonPlaceholder')}
              value={declining.reason}
              placeholder={say('hall.requests.reasonPlaceholder')}
              maxLength={200}
              onChange={(e) => setDeclining({ id: r.id, reason: e.target.value })}
            />
            <Button size="sm" variant="ghost" onPress={() => setDeclining(null)}>
              {say('common.keep')}
            </Button>
            <Button
              size="sm"
              variant="danger-soft"
              isDisabled={busy}
              onPress={() => void decide(r.id, false, declining.reason.trim() || undefined)}
            >
              {say('hall.requests.decline')}
            </Button>
          </span>
        ) : (
          <span className="fl-alert__acts">
            <Button size="sm" variant="ghost" isDisabled={busy} onPress={() => setDeclining({ id: r.id, reason: '' })}>
              {say('hall.requests.declineAsk')}
            </Button>
            <Button size="sm" variant="secondary" isDisabled={busy} onPress={() => void decide(r.id, true)}>
              {say('hall.requests.approve')}
            </Button>
          </span>
        ),
    }
  })
  const alertItems: AttentionItem[] = state
    ? alertsToShow(state, lang).map((a) => ({
        key: `alert-${a.queueId}`,
        level: 'serious',
        icon: 'alert',
        title: say(a.reason === 'waiting' ? 'notify.banner.waiting' : 'notify.banner.longest', {
          queue: a.queue,
          n: a.waiting,
          time: formatMoment(a.since, lang, { time: true, zone }),
        }),
        sub: say('floor.db.a.alertSub'),
      }))
    : []
  const lateItems: AttentionItem[] = late.length
    ? [
        {
          key: 'late',
          level: 'warning',
          icon: 'booking',
          title:
            late.length === 1
              ? say('hall.lateOne', { who: bookingWho(late[0]!), time: formatMoment(late[0]!.startsAt, lang, { time: true, zone }) })
              : say('hall.lateMany', { n: late.length }),
          sub: say('floor.db.a.lateSub'),
          action: <ActButton onPress={() => onNavigate('/appointments')}>{say('floor.db.a.bookings')}</ActButton>,
        },
      ]
    : []
  const at = firstWarning < 0 ? attention.length : firstWarning
  const allAttention = [...attention.slice(0, at), ...requestItems, ...alertItems, ...attention.slice(at), ...lateItems]

  const byRows: HBar[] = lines.map((l) => ({ key: l.q.queueId, label: queueName(l.q), value: l.line.length, shown: String(l.line.length), colour: l.colour }))
  const vsRows: HBar[] = lines
    .filter((l) => l.line.length > 0)
    .map((l) => ({
      key: l.q.queueId,
      label: queueName(l.q),
      value: l.avgWait,
      shown: minWord(l.avgWait, say),
      colour: l.colour,
      target: l.target || undefined,
      late: l.target > 0 && l.avgWait > l.target,
      title: l.target ? `${say('floor.db.warningTime')}: ${minWord(l.target, say)}` : undefined,
    }))

  const ticketOpen = openTicket ? tickets?.find((t) => t.id === openTicket && t.state === 'waiting') : undefined
  const deskOpen = openDesk ? open.find((r) => r.desk.counterId === openDesk) : undefined
  const journeyTicket = journey ? tickets?.find((t) => t.id === journey) : undefined
  const tileOfQueue = (t: Ticket) => {
    const q = t.queueId ? lineOf.get(t.queueId)?.q : undefined
    return q ? letter(q) : t.prefix.slice(0, 1)
  }

  // A desk tile's time in its state: on the break, since the call, or at the desk.
  const deskTime = (r: DeskRow) => {
    const st = deskState(r.desk)
    if (st === 'break') return mins(r.pausedSec, say)
    if (!r.desk.displayNumber) return ''
    if (r.desk.ticketState === 'called') {
      const t = r.desk.ticketId ? tickets?.find((x) => x.id === r.desk.ticketId) : undefined
      return mins(secondsSince(t?.lastCalledAt, now), say)
    }
    return r.desk.ticketState === 'serving' ? mins(servingSec(r.desk), say) : ''
  }
  const deskWords = (r: DeskRow) => {
    const st = deskState(r.desk)
    if (st === 'break') return pausedWords(r.desk.status, askedAt(r.desk.counterId), lang) || say('floor.db.d.break')
    if (st === 'serving' && r.desk.ticketState === 'called') return say('floor.db.d.calling')
    return say(`floor.db.d.${st}` as StaffKey)
  }

  return (
    <>
      {(error || live.error) && <Notice danger text={error || live.error} />}
      {!state && !live.error && <Loading />}
      {state && (
        <>
          <KpiRow
            waiting={waitingNow}
            served={served}
            avgWaitSec={waitingNow ? avgWait : null}
            avgTxSec={series && series.transactionsToday > 0 ? series.avgTransactionSecToday : null}
            longestSec={waitingNow ? longest : null}
            over={over}
            near={near}
            avgWaitLevel={waitKpiLevel(
              lines.filter((l) => l.line.length && slaLevel(l.avgWait, l.target) === 'critical').length,
              lines.filter((l) => l.line.length && slaLevel(l.avgWait, l.target) === 'warning').length,
            )}
            desksOpen={open.length}
            series={d}
          />
          <ChartRow series={d} times={times} targetSec={sharedTarget} attention={allAttention} />
          <TrendRow
            series={d}
            times={times}
            byTitle={say('floor.db.byQueue')}
            byRows={byRows}
            vsRows={vsRows}
            desks={deskCounts(counters ?? [])}
            desksCaption={say('floor.db.desksN', { n: desks.length })}
          />

          <Card className="fl-section" id="fl-lists">
            <div ref={listsRef} style={{ minHeight: listsMin || undefined }}>
            <Tabs selectedKey={tab} onSelectionChange={(k) => setTab(String(k) as ListTab)} className="fl-tabs">
              <Tabs.ListContainer>
                <Tabs.List aria-label={say('floor.db.lists')}>
                  {(
                    [
                      ['queues', say('floor.db.queues'), queues.length],
                      ['desks', say('floorBoard.desks'), desks.length],
                      ['waiting', say('floor.db.waitingList'), waitingNow],
                    ] as const
                  ).map(([id, label, n]) => (
                    <Tabs.Tab key={id} id={id}>
                      {label}
                      <Chip size="sm" className="fl-tabcount">
                        {n}
                      </Chip>
                      <Tabs.Indicator />
                    </Tabs.Tab>
                  ))}
                </Tabs.List>
              </Tabs.ListContainer>
              <Tabs.Panel id="desks" className="fl-panel">
            <p className="fl-panel__note">{say('floor.db.desksOpenOf', { open: open.length, n: desks.length })}</p>
            <Table variant="secondary" className="fl-scroll">
              <Table.ScrollContainer>
                <Table.Content
                  aria-label={say('floorBoard.desks')}
                  className="fl-table"
                  disabledKeys={desks.filter((r) => deskState(r.desk) === 'closed').map((r) => r.desk.counterId)}
                  onRowAction={(key) => {
                    setError('')
                    setOpenDesk(String(key))
                  }}
                >
                  <Table.Header>
                    <Table.Column isRowHeader>{say('floor.db.col.desk')}</Table.Column>
                    <Table.Column>{say('floor.db.col.status')}</Table.Column>
                    <Table.Column>{say('floor.db.col.clerk')}</Table.Column>
                    <Table.Column>{say('floor.db.col.profile')}</Table.Column>
                    <Table.Column>{say('floor.db.col.customer')}</Table.Column>
                    <Table.Column className="fl-n">{say('floor.db.col.time')}</Table.Column>
                  </Table.Header>
                  <Table.Body>
                    {desks.map((r) => {
                      const st = deskState(r.desk)
                      const time = deskTime(r)
                      return (
                        <Table.Row key={r.desk.counterId} id={r.desk.counterId} className="fl-desk" data-state={st}>
                          <Table.Cell>
                            <b>{r.desk.name}</b>
                          </Table.Cell>
                          <Table.Cell>
                            <Chip size="sm" variant="soft" color={DESK_COLOR[st]}>
                              {deskWords(r)}
                            </Chip>
                          </Table.Cell>
                          <Table.Cell>{r.desk.staffName || '—'}</Table.Cell>
                          <Table.Cell>{r.desk.profileName || (st === 'closed' ? '—' : say('floor.db.noProfile'))}</Table.Cell>
                          <Table.Cell>
                            {r.desk.displayNumber ? <b>{r.desk.displayNumber}</b> : st === 'free' ? say('hall.tile.waiting', { n: waitingFor(r) }) : '—'}
                          </Table.Cell>
                          <Table.Cell className="fl-n">
                            {time ? (
                              <span className="fl-wmin" data-level={servingSec(r.desk) > LONG_SERVING_SEC ? 'warning' : undefined}>
                                {time}
                              </span>
                            ) : (
                              '—'
                            )}
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
              </Tabs.Panel>

              <Tabs.Panel id="queues" className="fl-panel">
            <Table variant="secondary">
              <Table.ScrollContainer>
                <Table.Content aria-label={say('floor.db.queues')} className="fl-table">
                  <Table.Header>
                    <Table.Column isRowHeader>{say('floor.db.col.queue')}</Table.Column>
                    <Table.Column className="fl-n">{say('floor.db.col.waiting')}</Table.Column>
                    <Table.Column className="fl-n">
                      <span title={say('floor.db.queuesSub')}>{say('floor.db.col.openDesks')}</span>
                    </Table.Column>
                    <Table.Column className="fl-n">{say('floor.db.col.avgWait')}</Table.Column>
                    <Table.Column className="fl-n">{say('floor.db.col.avgTx')}</Table.Column>
                    <Table.Column className="fl-n">{say('floor.db.col.longest')}</Table.Column>
                    <Table.Column className="fl-n">{say('floor.db.col.targetMet')}</Table.Column>
                    <Table.Column>{say('floor.db.col.status')}</Table.Column>
                  </Table.Header>
                  <Table.Body>
                    {lines.map((l) => {
                      const met = l.line.length && l.target ? Math.round((100 * (l.line.length - l.over)) / l.line.length) : null
                      const work = workFor(l.q.queueId)
                      return (
                        <Table.Row key={l.q.queueId} id={l.q.queueId}>
                          <Table.Cell>
                            <span className="fl-name">
                              <span className="stub stub--sm">{letter(l.q)}</span>
                              <i className="fl-dot" style={{ background: l.colour }} />
                              {queueName(l.q)}
                            </span>
                          </Table.Cell>
                          <Table.Cell className="fl-n">{l.line.length}</Table.Cell>
                          <Table.Cell className="fl-n">{l.open}</Table.Cell>
                          <Table.Cell className="fl-n">{l.line.length ? minWord(l.avgWait, say) : '—'}</Table.Cell>
                          <Table.Cell className="fl-n">{work ? minWord(work.avgTransactionSec, say) : '—'}</Table.Cell>
                          <Table.Cell className="fl-n">
                            {l.line.length ? (
                              <span className="fl-wmin" data-level={l.over > 0 ? 'critical' : l.near > 0 ? 'warning' : undefined}>
                                {minWord(l.longest, say)}
                              </span>
                            ) : (
                              '—'
                            )}
                          </Table.Cell>
                          <Table.Cell className="fl-n">
                            {met === null ? '—' : <Share value={met} color={met < MET_WARN_PCT ? 'warning' : 'default'} />}
                          </Table.Cell>
                          <Table.Cell>
                            <StatusChip kind={queueStatus({ waiting: l.line.length, open: l.open, over: l.over, near: l.near }, known)} />
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
              </Tabs.Panel>

              <Tabs.Panel id="waiting" className="fl-panel">
            <Table variant="secondary" className="fl-scroll">
              <Table.ScrollContainer>
                <Table.Content aria-label={say('floor.db.waitingList')} className="fl-table" onRowAction={(key) => setOpenTicket(String(key))}>
                  <Table.Header>
                    <Table.Column isRowHeader>{say('floor.db.col.ticket')}</Table.Column>
                    <Table.Column>{say('floor.db.col.service')}</Table.Column>
                    <Table.Column>{say('floor.db.col.queue')}</Table.Column>
                    <Table.Column className="fl-n">{say('floor.db.col.inLine')}</Table.Column>
                    <Table.Column className="fl-n">{say('floor.db.col.waited')}</Table.Column>
                    <Table.Column className="fl-n">{say('floor.db.col.sla')}</Table.Column>
                  </Table.Header>
                  <Table.Body renderEmptyState={() => <p className="fl-quiet">{say('floor.db.nobodyWaiting')}</p>}>
                    {byWait.map((t) => {
                      const l = t.queueId ? lineOf.get(t.queueId) : undefined
                      const w = waitOf(t)
                      const level = slaLevel(w, targetOf(t))
                      const pos = (l?.line.findIndex((x) => x.id === t.id) ?? -1) + 1
                      const tag =
                        t.priority === 'vip' ? 'VIP' : t.priority === 'priority' ? say('hall.tag.priority') : t.appointmentAt ? say('hall.tag.booked') : ''
                      return (
                        <Table.Row key={t.id} id={t.id} className="fl-row">
                          <Table.Cell>
                            <span className="fl-name">
                              <span className="stub stub--sm">{tileOfQueue(t)}</span>
                              <b className="fl-ticket">{numberPart(t.displayNumber, tileOfQueue(t))}</b>
                            </span>
                          </Table.Cell>
                          <Table.Cell>
                            <span className="fl-name">
                              {serviceName(t) || '—'}
                              {tag && <Chip size="sm">{tag}</Chip>}
                              {sentChip(t)}
                            </span>
                          </Table.Cell>
                          <Table.Cell>{l ? queueName(l.q) : '—'}</Table.Cell>
                          <Table.Cell className="fl-n">{pos > 0 ? ordinal(pos) : '—'}</Table.Cell>
                          <Table.Cell className="fl-n">
                            <span className="fl-wmin" data-level={level}>
                              {mins(w, say)}
                            </span>
                          </Table.Cell>
                          <Table.Cell className="fl-n">
                            {targetOf(t) ? (
                              <span title={`${mins(w, say)} / ${mins(targetOf(t), say)}`}>
                                <Share
                                  value={slaUsed(w, targetOf(t))}
                                  color={level === 'critical' ? 'danger' : level === 'warning' ? 'warning' : 'success'}
                                />
                              </span>
                            ) : (
                              '—'
                            )}
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
              </Tabs.Panel>
            </Tabs>
            </div>
          </Card>
        </>
      )}

      {deskOpen && (
        <DeskPanel
          key={deskOpen.desk.counterId}
          row={deskOpen}
          tickets={tickets ?? []}
          queues={queues}
          letter={letter}
          calls={profileQueues(deskOpen.desk)}
          waitingFor={waitingFor(deskOpen)}
          asked={askedAt(deskOpen.desk.counterId)}
          now={now}
          zone={zone}
          busy={busy}
          error={error}
          services={snapshotServices ?? services}
          profiles={profiles}
          canSupervise={canSupervise}
          onProfile={(id) =>
            void act(() => api.changeSessionProfile(deskOpen.desk.counterId, id), say('eq.floor.changed', { desk: deskOpen.desk.name }))
          }
          onClose={() => setOpenDesk('')}
          onSignOut={() =>
            void act(() => api.closeCounter(deskOpen.desk.counterId), say('hall.done.signedOut', { desk: deskOpen.desk.name })).then(
              (ok) => ok && setOpenDesk(''),
            )
          }
        />
      )}

      {ticketOpen && (
        <TicketPanel
          key={ticketOpen.id}
          ticket={ticketOpen}
          line={lineOf.get(ticketOpen.queueId ?? '')?.line ?? [ticketOpen]}
          queue={queues.find((q) => q.queueId === ticketOpen.queueId)}
          letter={letter}
          calledBy={ticketOpen.lastCalledAt && ticketOpen.calledToCounterId ? deskName.get(ticketOpen.calledToCounterId) : undefined}
          serviceName={serviceName(ticketOpen)}
          wait={waitOf(ticketOpen)}
          target={targetOf(ticketOpen)}
          services={services}
          busy={busy}
          onClose={() => setOpenTicket('')}
          onMoved={() => {
            toast.show(say('hall.done.moved', { number: ticketOpen.displayNumber }))
            setOpenTicket('')
          }}
          onVoid={() =>
            void act(
              () => api.ticketAction(ticketOpen.id, 'void', { reason: 'withdrawn by a supervisor' }),
              say('hall.done.void', { number: ticketOpen.displayNumber }),
            ).then((ok) => ok && setOpenTicket(''))
          }
          onJourney={() => {
            setJourney(ticketOpen.id)
            setOpenTicket('')
          }}
        />
      )}

      {journeyTicket && (
        <JourneyPanel
          ticket={journeyTicket}
          serviceLabel={(id) => {
            const s = id ? serviceOf(id) : undefined
            return s ? nameIn(s.name, lang, s.code) : ''
          }}
          deskLabel={(id) => (id ? (deskName.get(id) ?? '') : '')}
          zone={zone}
          onClose={() => setJourney('')}
        />
      )}
      {toast.view}
    </>
  )
}

// The token actions the journey names; any other reads as Changed.
const TOKEN_KINDS = new Set(['ticket.recalled', 'ticket.moved', 'ticket.noted', 'ticket.customer_set', 'ticket.voided', 'ticket.unparked'])

/** A visit's path so far, from what its ticket carries (lib/liveDash journeySteps),
 *  with the steps an access token took marked from the ticket's history. */
function JourneyPanel({
  ticket,
  serviceLabel,
  deskLabel,
  zone,
  onClose,
}: {
  ticket: Ticket
  serviceLabel: (serviceId?: string) => string
  deskLabel: (counterId?: string) => string
  zone?: string
  onClose: () => void
}) {
  const { lang, say } = useStaffText()
  const steps = journeySteps(ticket)
  const [history, setHistory] = useState<TicketHistoryEvent[]>([])
  const changed = `${ticket.id}|${steps.length}|${ticket.lastCalledAt ?? ''}`
  useEffect(() => {
    let live = true
    api.ticketEvents(ticket.id).then(
      (r) => live && setHistory(r.events),
      () => {},
    )
    return () => {
      live = false
    }
  }, [ticket.id, changed])
  const marks = tokenMarks(steps, history)
  const rows = [
    ...steps.map((s, i) => {
      const service = serviceLabel(s.serviceId)
      const what =
        s.kind === 'started'
          ? say('floor.db.j.started', { service })
          : s.kind === 'ended'
            ? say('floor.db.j.ended', { service })
            : say(`floor.db.j.${s.kind}` as StaffKey)
      return { at: s.at, kind: s.kind as string, what, desk: deskLabel(s.counterId), token: marks.onStep[i] }
    }),
    ...marks.other.map((a) => {
      const what = say(TOKEN_KINDS.has(a.kind) ? (`floor.db.j.${a.kind}` as StaffKey) : 'floor.db.j.other')
      return { at: a.at, kind: a.kind, what, desk: '', token: a.token as string | undefined }
    }),
  ].sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
  return (
    <Drawer title={`${say('floor.db.journey')} · ${ticket.displayNumber}`} onClose={onClose} hideSubmit closeLabel={say('ui.close')}>
      <NoteHistory ticket={ticket} zone={zone} adds={false} edits={false} />
      <ol className="fl-journey">
        {rows.map((s, i) => (
          <li key={i} data-kind={s.kind}>
            <time>{formatMoment(s.at, lang, { time: true, zone })}</time>
            <span>
              <b>{s.what}</b>
              {s.desk && <small>{s.desk}</small>}
              {s.token && <small>{say('floor.db.j.viaToken', { name: s.token })}</small>}
            </span>
          </li>
        ))}
      </ol>
    </Drawer>
  )
}

/** The desk's number as it stands on the counter: the number its name ends in
 *  ("Desk 9" is 9), else its code. */
function deskMark(desk: CounterView): string {
  return desk.name.match(/(\d+)\s*$/)?.[1] ?? desk.code
}

type Say = ReturnType<typeof useStaffText>['say']

/** A desk's state in one line, split so the time sits apart from the words. The
 *  state names the tile's coloured edge. */
function tileOf(
  d: DeskRow,
  tickets: Ticket[],
  now: number,
  waiting: number,
  say: Say,
  lang: StaffLanguage,
  services: readonly Service[],
  asked: RequestKind | undefined,
): { state: 'break' | 'free' | 'calling' | 'serving'; left: ReactNode; right: string; over?: boolean } {
  const desk = d.desk
  if (desk.state === 'paused') {
    // The request waiting on a manager, or the kind of break running — org.DeriveStatus
    // by way of the server's `status`; with none, the plain "On a break".
    const headline = pausedWords(desk.status, asked, lang)
    return { state: 'break', left: headline || say('hall.tile.break'), right: mins(d.pausedSec, say) }
  }
  if (!desk.ticketId || !desk.displayNumber) {
    return { state: 'free', left: say('hall.tile.free'), right: waiting > 0 ? say('hall.tile.waiting', { n: waiting }) : '' }
  }
  const t = tickets.find((x) => x.id === desk.ticketId)
  const number = <strong>{desk.displayNumber}</strong>
  if (desk.ticketState === 'called') {
    const s = secondsSince(t?.lastCalledAt, now)
    return {
      state: 'calling',
      left: rich(say, 'hall.tile.calling', {}, { number }),
      right: s < 60 ? say('hall.now') : mins(s, say),
    }
  }
  if (desk.ticketState === 'wrapup') {
    return { state: 'serving', left: rich(say, 'hall.tile.wrapup', {}, { number }), right: '' }
  }
  const started = t?.transactions[t.currentTransaction]?.startedAt
  const over = overServiceLimit(t, services, now)
  const time = started ? mins(secondsSince(started, now), say) : ''
  return {
    state: 'serving',
    over,
    left: rich(say, 'hall.tile.serving', {}, { number }),
    right: over && time ? say('hall.tile.over', { time }) : time,
  }
}

/**
 * A desk opened from its tile: who is there, since when, what they call and what
 * they are doing, and signing them out. The tile says only the state.
 */
function DeskPanel({
  row,
  tickets,
  queues,
  letter,
  calls,
  waitingFor,
  asked,
  now,
  zone,
  busy,
  error,
  services,
  profiles,
  canSupervise,
  onProfile,
  onClose,
  onSignOut,
}: {
  row: DeskRow
  tickets: Ticket[]
  queues: QueueView[]
  letter: (q: QueueView) => string
  calls: string[]
  waitingFor: number
  asked: RequestKind | undefined
  now: number
  zone?: string
  busy: boolean
  error: string
  services: readonly Service[]
  profiles: WorkProfile[]
  canSupervise: boolean
  onProfile: (workProfileId: string) => void
  onClose: () => void
  onSignOut: () => void
}) {
  const { lang, say } = useStaffText()
  const [asking, setAsking] = useState(false)
  const desk = row.desk
  const tile = tileOf(row, tickets, now, waitingFor, say, lang, services, asked)
  const t = desk.ticketId ? tickets.find((x) => x.id === desk.ticketId) : undefined
  const doing: ReactNode = (() => {
    if (desk.state === 'paused') {
      const headline = pausedWords(desk.status, asked, lang)
      return headline
        ? say('hall.doing.paused', { what: headline, time: mins(row.pausedSec, say) })
        : say('hall.doing.break', { time: mins(row.pausedSec, say) })
    }
    if (!desk.displayNumber) return waitingFor > 0 ? say('hall.doing.free', { n: waitingFor }) : say('hall.doing.freeNobody')
    if (desk.ticketState === 'called') {
      const s = secondsSince(t?.lastCalledAt, now)
      return say('hall.doing.calling', { number: desk.displayNumber, time: s < 60 ? say('hall.now') : mins(s, say) })
    }
    if (desk.ticketState === 'wrapup') return say('hall.doing.wrapup', { number: desk.displayNumber })
    const started = t?.transactions[t.currentTransaction]?.startedAt
    return say('hall.doing.serving', { number: desk.displayNumber, time: mins(secondsSince(started, now), say) })
  })()
  const called = calls.flatMap((id) => queues.filter((q) => q.queueId === id))
  const may = mayChangeProfile(desk)
  const pick = canSupervise && may !== 'closed' && profiles.length > 1

  return (
    <Drawer
      title={desk.name}
      mark={
        <span className="icon-tile fl-dno" data-state={tile.state} aria-hidden="true">
          {deskMark(desk)}
        </span>
      }
      onClose={onClose}
      hideSubmit
      extra={
        row.offersSignOut && !asking ? (
          <Button size="sm" variant="danger-soft" onPress={() => setAsking(true)}>
            {say('floorBoard.signOut')}
          </Button>
        ) : undefined
      }
    >
      {asking && (
        <div className="hall-confirm" role="alert">
          <strong>{signOutQuestion(desk, lang)}</strong>
          <span className="hall-confirm__btns">
            <Button variant="danger" isDisabled={busy} onPress={onSignOut}>
              {say('floorBoard.signOut')}
            </Button>
            <Button variant="ghost" onPress={() => setAsking(false)}>
              {say('common.keep')}
            </Button>
          </span>
        </div>
      )}
      {error && <Notice danger text={error} />}
      <p className="fl-doing">{doing}</p>
      <dl className="hall-sum fl-sum">
        <div>
          <dt>{say('hall.clerk')}</dt>
          <dd>{desk.staffName || '—'}</dd>
        </div>
        <div>
          <dt>{say('hall.signedIn')}</dt>
          <dd>
            {desk.openedAt
              ? say('hall.since', {
                  time: formatMoment(desk.openedAt, lang, { time: true, zone }),
                  ago: mins(row.signedInSec, say),
                })
              : '—'}
          </dd>
        </div>
        <div>
          <dt>{say('hall.profile')}</dt>
          <dd>
            {pick ? (
              <span title={may === 'busy' ? say('eq.counter.busy') : undefined}>
                <Select
                  className="fl-profile"
                  variant="secondary"
                  aria-label={say('hall.profile')}
                  value={desk.workProfileId ?? null}
                  isDisabled={busy || may === 'busy'}
                  onChange={(id) => id != null && id !== desk.workProfileId && onProfile(String(id))}
                >
                  <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox aria-label={say('hall.profile')}>
                      {profiles.map((p) => (
                        <ListBox.Item key={p.id} id={p.id} textValue={p.name}>
                          <span className="fl-picker__opt">
                            <Label>{p.name}</Label>
                            <Description>{queues.filter((q) => p.queueIds.includes(q.queueId)).map(letter).join(' ')}</Description>
                          </span>
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>
              </span>
            ) : (
              desk.profileName || say('hall.none')
            )}
          </dd>
        </div>
        <div>
          <dt>{say('hall.calls')}</dt>
          <dd className="hall-sum__stubs">
            {called.length
              ? called.map((q) => (
                  <span key={q.queueId} className="stub stub--sm" title={nameIn(q.name, lang, q.code)}>
                    {letter(q)}
                  </span>
                ))
              : '—'}
          </dd>
        </div>
        {/* The calling rule that chose this customer: a supervisor's question, kept off the clerk's screen. */}
        {desk.displayNumber && ruleTag(desk.chosenBy, lang) && (
          <div>
            <dt>{say('hall.rule')}</dt>
            <dd>{ruleTag(desk.chosenBy, lang)}</dd>
          </div>
        )}
        {/* The Counter writes its break length as `break:<minutes>`; that is a
            marker, not a note, so it is not shown. Nor is a request's pause
            reason, which is only the request's kind, already the status. */}
        {desk.pauseReason &&
          desk.state === 'paused' &&
          !/^break(:\d{1,3})?$/.test(desk.pauseReason) &&
          !['pending', 'lunch', 'short_break', 'task'].includes(desk.status ?? '') && (
          <div className="hall-sum__wide">
            <dt>{say('hall.note')}</dt>
            <dd>{desk.pauseReason}</dd>
          </div>
        )}
        {desk.standingBySince && (
          <div className="hall-sum__wide">
            <dd>{say('hall.standingBy')}</dd>
          </div>
        )}
      </dl>
    </Drawer>
  )
}

/**
 * A waiting ticket opened from its line: how long, where in the line, the desk that
 * last called it if one did, and the two things a supervisor does to it — move it
 * (to another service, or to another place in its own line) or void it.
 */
function TicketPanel({
  ticket: t,
  line,
  queue,
  letter,
  calledBy,
  serviceName,
  wait,
  target,
  services,
  busy,
  onClose,
  onMoved,
  onVoid,
  onJourney,
}: {
  ticket: Ticket
  line: Ticket[]
  queue: QueueView | undefined
  letter: (q: QueueView) => string
  calledBy: string | undefined
  serviceName: string
  wait: number
  target: number
  services: Service[]
  busy: boolean
  onClose: () => void
  onMoved: () => void
  onVoid: () => void
  onJourney: () => void
}) {
  const { lang, say } = useStaffText()
  const [choice, setChoice] = useState<MoveChoice>({ serviceId: currentServiceId(t), placement: DEFAULT_PLACEMENT })
  const [refused, setRefused] = useState('')
  const moveBox = useRef<HTMLDivElement>(null)
  // Move is pressed in the footer; bring a refusal at the form's foot into view.
  useEffect(() => {
    if (refused) moveBox.current?.querySelector('.reception-error')?.scrollIntoView({ block: 'nearest' })
  }, [refused])
  const [voiding, setVoiding] = useState(false)
  const pos = line.findIndex((x) => x.id === t.id) + 1

  async function move() {
    const why = whyNotMove(currentServiceId(t), choice, Boolean(t.front), lang)
    if (why) {
      setRefused(why)
      return
    }
    try {
      await api.ticketAction(t.id, 'move', moveBody(choice))
      setRefused('')
      onMoved()
    } catch (err) {
      setRefused(explain(err, lang, 'common.unreachable'))
    }
  }

  return (
    <Drawer
      title={say('hall.ticket', { number: t.displayNumber })}
      mark={queue ? <span className="stub stub--lg">{letter(queue)}</span> : undefined}
      onClose={onClose}
      onSubmit={() => void move()}
      submitLabel={say('floor.move')}
      busy={busy}
      tone="light"
      closeLabel={say('ui.close')}
      extra={
        !voiding ? (
          <>
            <Button size="sm" variant="ghost" onPress={onJourney}>
              {say('floor.db.journey')}
            </Button>
            <Button size="sm" variant="danger-soft" onPress={() => setVoiding(true)}>
              {say('hall.voidTicket')}
            </Button>
          </>
        ) : undefined
      }
    >
      {voiding && (
        <div className="hall-confirm" role="alert">
          <strong>{say('floorBoard.voidQuestion', { number: t.displayNumber })}</strong>
          <span className="hall-confirm__btns">
            <Button variant="danger" isDisabled={busy} onPress={onVoid}>
              {say('floorBoard.void')}
            </Button>
            <Button variant="ghost" onPress={() => setVoiding(false)}>
              {say('common.keep')}
            </Button>
          </span>
        </div>
      )}
      <dl className="hall-sum">
        <div>
          <dt>{say('hall.cameFor')}</dt>
          <dd>{serviceName || '—'}</dd>
        </div>
        <div>
          <dt>{say('hall.waiting')}</dt>
          <dd data-over={(target > 0 && wait > target) || undefined}>
            {target > 0 ? say('hall.waitedOf', { time: mins(wait, say), target: mins(target, say) }) : mins(wait, say)}
          </dd>
        </div>
        <div>
          <dt>{say('hall.placeInLine')}</dt>
          <dd>{pos > 0 ? say('hall.nOfM', { n: pos, m: line.length }) : '—'}</dd>
        </div>
        {calledBy && (
          <div>
            <dt>{say('hall.calledByLabel')}</dt>
            <dd>{calledBy}</dd>
          </div>
        )}
        {t.note && (
          <div className="hall-sum__wide">
            <dt>{say('hall.note')}</dt>
            <dd>{t.note}</dd>
          </div>
        )}
      </dl>
      {/* Move and Close live in the dialog's footer; the form's own pair is hidden (floor.css). */}
      <div className="hall-move fl-move" ref={moveBox}>
        <h3 className="hall-move__head">{say('hall.move')}</h3>
        <MoveForm
          ticket={t}
          services={services}
          choice={choice}
          refused={refused}
          onChange={setChoice}
          onCancel={onClose}
          onMove={() => void move()}
        />
      </div>
    </Drawer>
  )
}
